import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isPasswordPwned } from '../../lib/passwordSecurity';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [passwordInfo, setPasswordInfo] = useState<{
    mustChange: boolean;
    deadlinePassed: boolean;
    deadline: string | null;
  } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkPasswordStatus();
  }, [user]);

  const checkPasswordStatus = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-password-requirement');

      if (error) throw error;

      setPasswordInfo(data);

      // Si le changement n'est pas requis, rediriger
      if (!data.mustChangePassword) {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    // Vérification anti-fuite : refuser les mots de passe compromis (HaveIBeenPwned)
    const pwned = await isPasswordPwned(newPassword);
    if (pwned) {
      setError('Ce mot de passe a été compromis lors d\'une fuite de données. Pour votre sécurité, choisissez-en un autre.');
      return;
    }

    setLoading(true);

    try {
      // Mettre à jour le mot de passe
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      // Réinitialiser les flags de changement de mot de passe
      if (user) {
        const { error: patientError } = await supabase
          .from('patients')
          .update({
            must_change_password: false,
            password_change_deadline: null,
          })
          .eq('user_id', user.id);

        if (patientError) {
          console.error('Erreur mise à jour patient:', patientError);
        }

        // ✅ NOUVEAU : Envoyer email/SMS de confirmation de changement de mot de passe
        try {
          const { data: patientData } = await supabase
            .from('patients')
            .select('nom, prenom, email, telephone')
            .eq('user_id', user.id)
            .single();

          if (patientData) {
            const userName = `${patientData.prenom} ${patientData.nom}`;
            const hasEmail = patientData.email;
            const hasPhone = patientData.telephone;

            // Privilégier l'email si disponible
            if (hasEmail) {
              await supabase.functions.invoke('send-email', {
                body: {
                  to: patientData.email,
                  toName: userName,
                  subject: 'Confirmation de changement de mot de passe',
                  htmlContent: `
                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                      <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                        <h1 style="color: white; margin: 0;">🔐 Sécurité du compte</h1>
                      </div>
                      <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                        <h2 style="color: #059669;">Mot de passe modifié avec succès</h2>
                        <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                          Bonjour ${userName},
                        </p>
                        <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                          Votre mot de passe a été modifié avec succès le ${new Date().toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}.
                        </p>
                        <div style="background: #d1fae5; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; border-radius: 5px;">
                          <p style="color: #065f46; margin: 0; font-size: 14px;">
                            ✅ Votre compte est maintenant sécurisé avec votre nouveau mot de passe.
                          </p>
                        </div>
                        <p style="color: #ef4444; font-size: 14px; line-height: 1.6; margin-top: 20px;">
                          ⚠️ Si vous n'êtes pas à l'origine de cette modification, contactez immédiatement notre service.
                        </p>
                        <div style="text-align: center; margin: 30px 0;">
                          <a href="${window.location.origin}/login" 
                             style="background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; display: inline-block;">
                            Se connecter
                          </a>
                        </div>
                      </div>
                    </div>
                  `
                }
              });
              console.log('✅ Email de confirmation changement mot de passe envoyé');
            } else if (hasPhone) {
              // Sinon envoyer un SMS
              await supabase.functions.invoke('send-sms', {
                body: {
                  to: patientData.telephone,
                  message: `Clinique Sainte-Famille : Votre mot de passe a été modifié avec succès le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}. Si ce n'est pas vous, contactez-nous immédiatement.`
                }
              });
              console.log('✅ SMS de confirmation changement mot de passe envoyé');
            }
          }
        } catch (notifError) {
          console.error('⚠️ Erreur envoi notification changement mot de passe:', notifError);
        }
      }

      alert('Mot de passe modifié avec succès !');
      
      // Recharger les exigences de mot de passe
      await checkPasswordStatus();
      
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Vérification...</p>
        </div>
      </div>
    );
  }

  const timeRemaining = passwordInfo?.deadline
    ? Math.max(0, Math.floor((new Date(passwordInfo.deadline).getTime() - Date.now()) / (1000 * 60 * 60)))
    : 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-3xl text-red-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-clinic-blue-dark">Changement de mot de passe obligatoire</h2>
              <p className="text-gray-600 mt-2">Votre compte a été créé par le personnel médical</p>
            </div>

            {passwordInfo?.deadlinePassed ? (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                  <div className="text-sm text-red-800">
                    <p className="font-semibold mb-1">⚠️ Délai dépassé</p>
                    <p>Le délai de 24h pour changer votre mot de passe est dépassé. Votre compte est temporairement bloqué.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <i className="ri-time-line text-xl text-yellow-600 mt-0.5"></i>
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">⏰ Temps restant : {timeRemaining}h</p>
                    <p>Pour des raisons de sécurité, vous devez changer votre mot de passe temporaire dans les 24h suivant la création de votre compte.</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Nouveau mot de passe
                </label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  placeholder="Minimum 6 caractères"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Confirmer le nouveau mot de passe
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  placeholder="Retapez votre mot de passe"
                />
              </div>

              <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                <p className="text-sm text-blue-800">
                  <i className="ri-shield-check-line mr-2"></i>
                  Choisissez un mot de passe fort que vous seul connaissez
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? 'Changement en cours...' : 'Changer mon mot de passe'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
